/**
 * collection-load-more.js
 *
 * Handles "Load More" pagination for collection pages.
 * - Fetches the next page silently via fetch()
 * - Appends new product <li> items to the existing grid
 * - Updates the button's next URL (or removes it when no more pages)
 * - Shows a loading spinner while fetching
 */

(function () {
  'use strict';

  /**
   * Initialise load-more behaviour inside a given root element.
   * Called once on DOMContentLoaded, and again after filters/sorting
   * cause a partial re-render (if your theme dispatches a custom event).
   */
  function initLoadMore(root) {
    const btn = root.querySelector('[data-load-more-btn]');
    if (!btn) return;

    // Prevent double-binding if already initialised
    if (btn.dataset.loadMoreReady) return;
    btn.dataset.loadMoreReady = 'true';

    btn.addEventListener('click', handleLoadMore);
  }

  async function handleLoadMore(e) {
    const btn = e.currentTarget;
    const nextUrl = btn.dataset.nextUrl;
    if (!nextUrl) return;

    // Find the product list that lives in the same collection-product-grid
    const grid = btn.closest('[id="main-collection-product-grid"]');
    const productList = grid ? grid.querySelector('[data-product-list]') : null;
    if (!productList) return;

    setLoading(btn, true);

    try {
      const response = await fetch(nextUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Grab new product items from the fetched page
      const newItems = doc.querySelectorAll('[data-product-list] [data-collection-product]');

      // Append each new item with a small fade-in
      newItems.forEach((item) => {
        item.style.opacity = '0';
        item.style.transition = 'opacity 0.3s ease';
        productList.appendChild(item);
        // Trigger reflow so the transition fires
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            item.style.opacity = '1';
          });
        });
      });

      // Check if there is yet another page after this one
      const nextBtn = doc.querySelector('[data-load-more-btn]');

      if (nextBtn) {
        // Update URL to the next-next page
        btn.dataset.nextUrl = nextBtn.dataset.nextUrl;
        setLoading(btn, false);
      } else {
        // No more pages — remove the button gracefully
        const wrapper = btn.closest('.pagination-wrapper');
        if (wrapper) {
          wrapper.style.transition = 'opacity 0.3s ease';
          wrapper.style.opacity = '0';
          setTimeout(() => wrapper.remove(), 300);
        } else {
          btn.remove();
        }
      }
    } catch (err) {
      console.error('[LoadMore] Failed to fetch next page:', err);
      setLoading(btn, false);
    }
  }

  function setLoading(btn, isLoading) {
    const label = btn.querySelector('[data-load-more-label]');
    const spinner = btn.querySelector('[data-load-more-spinner]');

    btn.disabled = isLoading;
    btn.setAttribute('aria-busy', isLoading);

    if (label) label.style.visibility = isLoading ? 'hidden' : 'visible';
    if (spinner) spinner.style.display = isLoading ? 'inline-block' : 'none';
  }

  // Initialise on page load
  document.addEventListener('DOMContentLoaded', () => {
    initLoadMore(document);
  });

  // Re-initialise if your theme re-renders the collection grid
  // (e.g. after filtering/sorting via AJAX). Adjust the event name
  // to match whatever your theme dispatches.
  document.addEventListener('collection:rerender', () => {
    initLoadMore(document);
  });
})();
